import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  BookOpen,
  Play,
  ShoppingCart,
  Check,
  Loader2,
  Star,
  Filter,
  X,
  Lock,
  Unlock,
  CreditCard,
  ChevronRight,
  GraduationCap,
  Plus,
  Minus,
  Sparkles,
  Gift,
  Calculator,
  Shapes,
  Hash,
  Trophy,
  Zap,
  TrendingUp,
  Flame,
  Users,
  Timer,
  Tag,
  Percent
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for popular bundles
const BUNDLE_ICONS = {
  calculator: Calculator,
  shapes: Shapes,
  hash: Hash,
  trophy: Trophy
};

export default function StorePage() {
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [myPurchases, setMyPurchases] = useState([]);
  const [bundles, setBundles] = useState(null);
  const [popularBundles, setPopularBundles] = useState([]);
  const [selectedPopularBundle, setSelectedPopularBundle] = useState(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [flashSales, setFlashSales] = useState([]);
  
  // Cart state for bundle purchases
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [bundleTier, setBundleTier] = useState('cards');
  const [bundleEmail, setBundleEmail] = useState('');
  
  // Checkout state for manual link fallback
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [showCheckoutFallback, setShowCheckoutFallback] = useState(false);
  
  // Check for cancelled payment
  const wasCancelled = searchParams.get('cancelled') === 'true';

  useEffect(() => {
    fetchTopics();
    fetchPopularBundles();
    fetchBestSellers();
    fetchFlashSales();
    if (isAuthenticated) {
      fetchMyPurchases();
    }
  }, [isAuthenticated]);

  const fetchTopics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/store/topics`);
      if (response.ok) {
        const data = await response.json();
        setTopics(data.topics);
        setGrades(data.grades);
        setBundles(data.bundles);
      }
    } catch (err) {
      console.error('Error fetching topics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPopularBundles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/store/popular-bundles`);
      if (response.ok) {
        const data = await response.json();
        setPopularBundles(data.popular_bundles || []);
      }
    } catch (err) {
      console.error('Error fetching popular bundles:', err);
    }
  };

  const fetchBestSellers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/store/best-sellers`);
      if (response.ok) {
        const data = await response.json();
        setBestSellers(data.best_sellers || []);
        setRecentPurchases(data.recent_purchases || []);
        setTotalPurchases(data.total_purchases || 0);
      }
    } catch (err) {
      console.error('Error fetching best sellers:', err);
    }
  };

  const fetchFlashSales = async () => {
    try {
      const response = await fetch(`${API_URL}/api/store/flash-sales`);
      if (response.ok) {
        const data = await response.json();
        setFlashSales(data.flash_sales || []);
      }
    } catch (err) {
      console.error('Error fetching flash sales:', err);
    }
  };

  const fetchMyPurchases = async () => {
    try {
      const response = await fetch(`${API_URL}/api/store/my-purchases`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMyPurchases(data.purchases);
      }
    } catch (err) {
      console.error('Error fetching purchases:', err);
    }
  };

  const isPurchased = (topicId) => {
    return myPurchases.some(p => p.topic_id === topicId);
  };

  const getPurchaseTier = (topicId) => {
    const purchase = myPurchases.find(p => p.topic_id === topicId);
    return purchase?.tier;
  };

  // Cart functions
  const isInCart = (topicId) => cart.includes(topicId);
  
  const toggleCart = (topicId) => {
    if (isPurchased(topicId)) return;
    if (isInCart(topicId)) {
      setCart(cart.filter(id => id !== topicId));
    } else {
      setCart([...cart, topicId]);
    }
  };

  const clearCart = () => {
    setCart([]);
    setShowCart(false);
  };

  const getBundlePrice = () => {
    const count = cart.length;
    if (count < 3) return null;
    
    if (bundleTier === 'cards') {
      if (count >= 5) return { price: bundles?.bundle_5_cards?.price || 3.99, savings: bundles?.bundle_5_cards?.savings || 0.96 };
      return { price: bundles?.bundle_3_cards?.price || 2.50, savings: bundles?.bundle_3_cards?.savings || 0.47 };
    } else {
      if (count >= 5) return { price: bundles?.bundle_5_videos?.price || 14.99, savings: bundles?.bundle_5_videos?.savings || 4.96 };
      return { price: bundles?.bundle_3_videos?.price || 9.99, savings: bundles?.bundle_3_videos?.savings || 1.98 };
    }
  };

  const getRegularPrice = () => {
    const count = cart.length;
    if (bundleTier === 'cards') return count * 0.99;
    return count * 3.99;
  };

  const handleBundlePurchase = async () => {
    if (cart.length < 3) {
      alert('Select at least 3 topics for a bundle discount');
      return;
    }
    
    const email = isAuthenticated ? user?.email : bundleEmail;
    if (!isAuthenticated && !bundleEmail) {
      alert('Please enter your email address');
      return;
    }

    setPurchasing(true);
    setCheckoutUrl(null);
    setShowCheckoutFallback(false);
    
    try {
      const response = await fetch(`${API_URL}/api/store/bundle-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic_ids: cart,
          tier: bundleTier,
          email: email
        })
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // Store checkout info for recovery
        localStorage.setItem('pending_checkout', JSON.stringify({
          url: data.checkout_url,
          bundle_id: data.bundle_id,
          timestamp: Date.now()
        }));
        
        // Show the payment link modal (don't auto-redirect - causes issues in in-app browsers)
        setCheckoutUrl(data.checkout_url);
        setPurchasing(false);
      } else {
        alert(data.detail || 'Failed to create bundle checkout');
        setPurchasing(false);
        setCheckoutUrl(null);
        setShowCheckoutFallback(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error creating bundle checkout. Please try again.');
      setPurchasing(false);
      setCheckoutUrl(null);
      setShowCheckoutFallback(false);
    }
  };

  // Track topic view for flash sale notifications
  const trackTopicView = async (topicId) => {
    try {
      await fetch(`${API_URL}/api/store/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic_id: topicId,
          email: user?.email
        })
      });
    } catch (err) {
      // Silent fail - tracking is not critical
    }
  };

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    trackTopicView(topic.topic_id);
  };

  const handlePopularBundlePurchase = async (bundle, tier, email) => {
    const topicIds = bundle.topics.map(t => t.topic_id);
    setPurchasing(true);
    setCheckoutUrl(null);
    setShowCheckoutFallback(false);
    
    try {
      const response = await fetch(`${API_URL}/api/store/bundle-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic_ids: topicIds,
          tier: tier,
          email: email
        })
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // Store checkout info for recovery
        localStorage.setItem('pending_checkout', JSON.stringify({
          url: data.checkout_url,
          bundle_id: data.bundle_id,
          timestamp: Date.now()
        }));
        
        // Show the payment link modal (don't auto-redirect - causes issues in in-app browsers)
        setCheckoutUrl(data.checkout_url);
        setPurchasing(false);
      } else {
        alert(data.detail || 'Failed to create bundle checkout');
        setPurchasing(false);
        setCheckoutUrl(null);
        setShowCheckoutFallback(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error creating bundle checkout. Please try again.');
      setPurchasing(false);
      setCheckoutUrl(null);
      setShowCheckoutFallback(false);
    }
  };

  const handlePurchase = async (topicId, tier, email = null) => {
    setPurchasing(true);
    setCheckoutUrl(null);
    setShowCheckoutFallback(false);
    
    try {
      const response = await fetch(`${API_URL}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          topic_id: topicId,
          tier: tier,
          email: email
        })
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // Store checkout info for recovery
        localStorage.setItem('pending_checkout', JSON.stringify({
          url: data.checkout_url,
          purchase_id: data.purchase_id,
          timestamp: Date.now()
        }));
        
        // Show the payment link modal (don't auto-redirect - causes issues in in-app browsers)
        setCheckoutUrl(data.checkout_url);
        setPurchasing(false);
      } else {
        alert(data.detail || 'Failed to create checkout');
        setPurchasing(false);
        setCheckoutUrl(null);
        setShowCheckoutFallback(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error creating checkout. Please try again.');
      setPurchasing(false);
      setCheckoutUrl(null);
      setShowCheckoutFallback(false);
    }
  };

  const filteredTopics = selectedGrade === 'all' 
    ? topics 
    : topics.filter(t => t.grade === selectedGrade);

  // Group topics by grade
  const topicsByGrade = filteredTopics.reduce((acc, topic) => {
    if (!acc[topic.grade]) acc[topic.grade] = [];
    acc[topic.grade].push(topic);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCF5] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFCF5]">
      {/* Full-screen Payment Link Modal */}
      {checkoutUrl && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full text-center shadow-2xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#0F172A] mb-2">
              Payment Ready!
            </h3>
            <p className="text-[#64748B] text-sm mb-6">
              Your secure payment page is ready. Open it in your browser to complete the purchase.
            </p>
            
            <div className="space-y-3">
              <a 
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-4 px-4 bg-[#C5A059] hover:bg-[#B8934E] text-white rounded-xl font-semibold transition-colors text-lg"
              >
                Open Payment Page →
              </a>
              
              <button
                onClick={() => {
                  navigator.clipboard.writeText(checkoutUrl);
                  alert('Payment link copied! Paste it in your browser.');
                }}
                className="w-full py-3 px-4 border-2 border-[#E2E8F0] rounded-xl text-[#0F172A] font-medium hover:border-[#C5A059] transition-colors"
              >
                📋 Copy Payment Link
              </button>
              
              <button
                onClick={() => {
                  setCheckoutUrl(null);
                  setPurchasing(false);
                  setShowCheckoutFallback(false);
                }}
                className="text-sm text-[#64748B] hover:text-[#0F172A] underline mt-2"
              >
                Cancel
              </button>
            </div>
            
            <p className="text-xs text-[#94A3B8] mt-4">
              💡 Tip: For best results, open the link in Chrome or Safari
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#0F172A] hover:text-[#C5A059]">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <h1 className="font-['Playfair_Display'] text-xl font-bold text-[#0F172A]">
            Topic Store
          </h1>
          {isAuthenticated ? (
            <Link to="/dashboard" className="text-[#C5A059] font-medium hover:underline">
              My Dashboard
            </Link>
          ) : (
            <Link to="/login" className="text-[#C5A059] font-medium hover:underline">
              Sign In
            </Link>
          )}
        </div>
      </header>

      {/* Cancelled Payment Notice */}
      {wasCancelled && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <X className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800">Payment was cancelled. Feel free to try again when you're ready!</p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-4">
            GCSE Revision Resources
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-6">
            Master each topic with expert revision cards and walkthrough videos. 
            Less than the price of a snack bar!
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <div className="bg-white/10 backdrop-blur rounded-xl px-6 py-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <BookOpen className="w-5 h-5 text-[#C5A059]" />
                <span className="text-2xl font-bold text-white">£0.99</span>
              </div>
              <p className="text-white/70 text-sm">Revision Cards Only</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl px-6 py-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Play className="w-5 h-5 text-[#C5A059]" />
                <span className="text-2xl font-bold text-white">£3.99</span>
              </div>
              <p className="text-white/70 text-sm">Cards + Videos Bundle</p>
            </div>
          </div>
          
          {/* Bundle Deal Banner */}
          <div className="mt-8 bg-gradient-to-r from-[#C5A059] to-[#D4AF37] rounded-xl p-4 md:p-6 text-center">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <Gift className="w-6 h-6 text-white" />
                <span className="text-white font-bold text-lg">BUNDLE DEAL!</span>
              </div>
              <div className="text-white/90">
                <span className="font-semibold">Any 3 topics for £2.50</span>
                <span className="mx-2">•</span>
                <span className="font-semibold">Any 5 topics for £3.99</span>
                <span className="text-white/70 ml-2">(Save up to £0.96!)</span>
              </div>
              <Button 
                onClick={() => setShowCart(true)}
                className="bg-white text-[#0F172A] hover:bg-white/90 font-semibold"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Build Bundle {cart.length > 0 && `(${cart.length})`}
              </Button>
            </div>
          </div>

          {/* Fun Price Comparison Banner */}
          <div className="mt-6 bg-white/10 backdrop-blur rounded-xl p-4 md:p-6">
            <p className="text-white/90 text-center font-semibold mb-4 text-lg">
              🎮 Cheaper than your favourite game packs & snacks! 🍫
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <div className="bg-[#0F172A]/50 px-4 py-2 rounded-full text-white/80 flex items-center gap-2">
                <span>🎮</span>
                <span>FIFA FC Points: <span className="line-through text-red-400">£8.99+</span></span>
              </div>
              <div className="bg-[#0F172A]/50 px-4 py-2 rounded-full text-white/80 flex items-center gap-2">
                <span>🎯</span>
                <span>Fortnite V-Bucks: <span className="line-through text-red-400">£6.49+</span></span>
              </div>
              <div className="bg-[#0F172A]/50 px-4 py-2 rounded-full text-white/80 flex items-center gap-2">
                <span>⛏️</span>
                <span>Minecraft Packs: <span className="line-through text-red-400">£4.99+</span></span>
              </div>
              <div className="bg-[#0F172A]/50 px-4 py-2 rounded-full text-white/80 flex items-center gap-2">
                <span>🚗</span>
                <span>GTA Shark Cards: <span className="line-through text-red-400">£11.99+</span></span>
              </div>
              <div className="bg-[#0F172A]/50 px-4 py-2 rounded-full text-white/80 flex items-center gap-2">
                <span>🍫</span>
                <span>Snickers Bar: <span className="line-through text-red-400">£1.20</span></span>
              </div>
              <div className="bg-[#0F172A]/50 px-4 py-2 rounded-full text-white/80 flex items-center gap-2">
                <span>🍬</span>
                <span>Haribo Bag: <span className="line-through text-red-400">£1.50</span></span>
              </div>
            </div>
            <p className="text-center mt-4">
              <span className="text-[#C5A059] font-bold text-xl">Revision cards from just 99p!</span>
              <span className="text-white/70 block text-sm mt-1">Better for your grades AND your wallet 🧠💰</span>
            </p>
          </div>
        </div>
      </div>

      {/* Floating Cart Button (mobile) */}
      {cart.length > 0 && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 z-40 bg-[#C5A059] text-white p-4 rounded-full shadow-lg hover:bg-[#B8934E] md:hidden"
        >
          <ShoppingCart className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-[#0F172A] text-white text-xs font-bold rounded-full flex items-center justify-center">
            {cart.length}
          </span>
        </button>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        
        {/* Flash Sales Section */}
        {flashSales.length > 0 && (
          <div className="mb-12">
            <div className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 rounded-2xl p-1">
              <div className="bg-white rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <Tag className="w-5 h-5 text-white" />
                    </div>
                    Flash Sales
                    <span className="text-sm font-normal text-white bg-red-500 px-3 py-1 rounded-full animate-pulse">
                      Limited Time!
                    </span>
                  </h2>
                </div>
                
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {flashSales.map(sale => (
                    <FlashSaleCard
                      key={sale.topic_id}
                      sale={sale}
                      isPurchased={isPurchased(sale.topic_id)}
                      onSelect={() => handleTopicSelect({
                        ...sale,
                        flash_sale: sale.flash_sale
                      })}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Popular Bundles Section */}
        {popularBundles.length > 0 && (
          <div className="mb-12">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <Zap className="w-7 h-7 text-[#C5A059]" />
              Popular Bundles
              <span className="text-sm font-normal text-white bg-[#C5A059] px-2 py-0.5 rounded-full">
                Best Value
              </span>
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {popularBundles.map(bundle => {
                const IconComponent = BUNDLE_ICONS[bundle.icon] || Gift;
                return (
                  <div 
                    key={bundle.bundle_slug}
                    onClick={() => setSelectedPopularBundle(bundle)}
                    className="bg-white rounded-xl border-2 border-[#E2E8F0] hover:border-[#C5A059] transition-all cursor-pointer overflow-hidden group"
                  >
                    {/* Gradient Header */}
                    <div className={`bg-gradient-to-r ${bundle.color} p-4`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{bundle.name}</h3>
                          <p className="text-white/80 text-xs">{bundle.topic_count} topics</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <p className="text-[#64748B] text-sm mb-3 line-clamp-2">{bundle.description}</p>
                      
                      {/* Pricing */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-lg font-bold text-[#C5A059]">
                            £{bundle.pricing.cards.price.toFixed(2)}
                          </span>
                          <span className="text-xs text-[#64748B] line-through ml-2">
                            £{bundle.pricing.cards.regular_price.toFixed(2)}
                          </span>
                        </div>
                        <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                          Save £{bundle.pricing.cards.savings.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Best Sellers / Trending Section */}
        {bestSellers.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] flex items-center gap-3">
                <Flame className="w-7 h-7 text-orange-500" />
                Trending Topics
                {totalPurchases > 0 && (
                  <span className="text-sm font-normal text-white bg-orange-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {totalPurchases}+ sold
                  </span>
                )}
              </h2>
              
              {/* Live Purchase Feed */}
              {recentPurchases.length > 0 && (
                <div className="hidden md:flex items-center gap-2 text-sm text-[#64748B] bg-[#F8FAFC] px-3 py-2 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span>Someone just bought <strong className="text-[#0F172A]">{recentPurchases[0]?.topic_name}</strong></span>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {bestSellers.slice(0, 4).map((topic, idx) => (
                <div 
                  key={topic.topic_id}
                  onClick={() => handleTopicSelect(topic)}
                  className="bg-white rounded-xl border-2 border-[#E2E8F0] hover:border-orange-400 transition-all cursor-pointer overflow-hidden group relative"
                >
                  {/* Ranking Badge */}
                  <div className="absolute top-3 left-3 z-10 w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-lg">
                    {idx + 1}
                  </div>
                  
                  {/* Preview */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-orange-400/20 to-red-400/20 relative">
                    {topic.preview_image ? (
                      <img 
                        src={topic.preview_image.url || topic.preview_image} 
                        alt={topic.topic_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <TrendingUp className="w-12 h-12 text-orange-400/40" />
                      </div>
                    )}
                    
                    {/* Hot Badge for top 2 */}
                    {idx < 2 && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <Flame className="w-3 h-3" />
                        HOT
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-3">
                    <h3 className="font-semibold text-[#0F172A] text-sm line-clamp-1 mb-1">
                      {topic.topic_name}
                    </h3>
                    <p className="text-xs text-[#64748B] mb-2">{topic.grade}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[#C5A059] font-bold">£0.99</span>
                      {topic.purchase_count > 0 && (
                        <span className="text-xs text-[#64748B] flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {topic.purchase_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bundle Selection Notice */}
        {cart.length > 0 && cart.length < 3 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800">
              Select <strong>{3 - cart.length} more topic{3 - cart.length > 1 ? 's' : ''}</strong> to unlock bundle pricing!
            </p>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-4 mb-8">
          <Filter className="w-5 h-5 text-[#64748B]" />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedGrade('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedGrade === 'all'
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#0F172A]'
              }`}
            >
              All Grades
            </button>
            {grades.map(grade => (
              <button
                key={grade}
                onClick={() => setSelectedGrade(grade)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedGrade === grade
                    ? 'bg-[#0F172A] text-white'
                    : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#0F172A]'
                }`}
              >
                {grade}
              </button>
            ))}
          </div>
        </div>

        {/* Topics by Grade */}
        {Object.entries(topicsByGrade).map(([grade, gradeTopics]) => (
          <div key={grade} className="mb-12">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <GraduationCap className="w-7 h-7 text-[#C5A059]" />
              {grade} Topics
              <span className="text-base font-normal text-[#64748B]">
                ({gradeTopics.length} topics)
              </span>
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {gradeTopics.map(topic => (
                <TopicCard
                  key={topic.topic_id}
                  topic={topic}
                  isPurchased={isPurchased(topic.topic_id)}
                  purchaseTier={getPurchaseTier(topic.topic_id)}
                  isInCart={isInCart(topic.topic_id)}
                  onToggleCart={() => toggleCart(topic.topic_id)}
                  onSelect={() => handleTopicSelect(topic)}
                  onPurchase={handlePurchase}
                  purchasing={purchasing}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {topics.length === 0 && (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-[#C5A059]/40 mx-auto mb-4" />
            <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-2">
              Coming Soon!
            </h3>
            <p className="text-[#64748B]">
              We're preparing amazing revision content. Check back soon!
            </p>
          </div>
        )}
      </div>

      {/* Topic Detail Modal */}
      {selectedTopic && (
        <TopicModal
          topic={selectedTopic}
          isPurchased={isPurchased(selectedTopic.topic_id)}
          purchaseTier={getPurchaseTier(selectedTopic.topic_id)}
          onClose={() => setSelectedTopic(null)}
          onPurchase={handlePurchase}
          purchasing={purchasing}
          isAuthenticated={isAuthenticated}
          userEmail={user?.email}
        />
      )}

      {/* Bundle Cart Modal */}
      {showCart && (
        <BundleCartModal
          cart={cart}
          topics={topics}
          bundleTier={bundleTier}
          setBundleTier={setBundleTier}
          bundleEmail={bundleEmail}
          setBundleEmail={setBundleEmail}
          isAuthenticated={isAuthenticated}
          userEmail={user?.email}
          onRemove={(id) => setCart(cart.filter(i => i !== id))}
          onClose={() => setShowCart(false)}
          onClear={clearCart}
          onCheckout={handleBundlePurchase}
          purchasing={purchasing}
          getBundlePrice={getBundlePrice}
          getRegularPrice={getRegularPrice}
        />
      )}

      {/* Popular Bundle Modal */}
      {selectedPopularBundle && (
        <PopularBundleModal
          bundle={selectedPopularBundle}
          onClose={() => setSelectedPopularBundle(null)}
          onPurchase={handlePopularBundlePurchase}
          purchasing={purchasing}
          isAuthenticated={isAuthenticated}
          userEmail={user?.email}
        />
      )}
    </div>
  );
}

// ============ COUNTDOWN TIMER COMPONENT ============
function CountdownTimer({ endTime }) {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const end = new Date(endTime);
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft('Ended');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        setTimeLeft(`${days}d ${hours % 24}h left`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m left`);
      } else {
        setTimeLeft(`${minutes}m ${seconds}s left`);
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [endTime]);
  
  return (
    <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
      <Timer className="w-3 h-3" />
      {timeLeft}
    </span>
  );
}

// ============ FLASH SALE CARD COMPONENT ============
function FlashSaleCard({ sale, isPurchased, onSelect }) {
  const flash = sale.flash_sale;
  
  return (
    <div 
      onClick={onSelect}
      className={`bg-white rounded-xl border-2 overflow-hidden transition-all cursor-pointer ${
        isPurchased ? 'border-green-300' : 'border-red-200 hover:border-red-400 hover:shadow-lg'
      }`}
    >
      {/* Sale Banner */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Percent className="w-4 h-4 text-white" />
          <span className="text-white font-bold text-sm">
            {flash.discount_percent}% OFF
          </span>
        </div>
        {flash.sale_end && <CountdownTimer endTime={flash.sale_end} />}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <span className="text-xs text-red-500 font-semibold">{flash.sale_label}</span>
            <h3 className="font-semibold text-[#0F172A] line-clamp-1">{sale.topic_name}</h3>
          </div>
          {isPurchased && (
            <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
              Owned
            </span>
          )}
        </div>
        
        <p className="text-xs text-[#64748B] mb-3">{sale.grade} • {sale.tier}</p>
        
        {!isPurchased && (
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-bold text-red-500">
                £{flash.sale_price_cards.toFixed(2)}
              </span>
              <span className="text-sm text-[#64748B] line-through">
                £{flash.original_price_cards.toFixed(2)}
              </span>
            </div>
            <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
              Save £{flash.savings_cards.toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ POPULAR BUNDLE MODAL ============
function PopularBundleModal({ bundle, onClose, onPurchase, purchasing, isAuthenticated, userEmail }) {
  const [selectedTier, setSelectedTier] = useState('cards');
  const [guestEmail, setGuestEmail] = useState('');
  const IconComponent = BUNDLE_ICONS[bundle.icon] || Gift;

  const handleBuy = () => {
    const email = isAuthenticated ? userEmail : guestEmail;
    if (!isAuthenticated && !guestEmail) {
      alert('Please enter your email address');
      return;
    }
    onPurchase(bundle, selectedTier, email);
  };

  const pricing = selectedTier === 'cards' ? bundle.pricing.cards : bundle.pricing.cards_videos;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header with Gradient */}
        <div className={`bg-gradient-to-r ${bundle.color} p-6 relative`}>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <IconComponent className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-white">
                {bundle.name}
              </h2>
              <p className="text-white/80">{bundle.topic_count} topics included</p>
            </div>
          </div>
          <p className="text-white/90">{bundle.description}</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Topics List */}
          <div className="mb-6">
            <h3 className="font-semibold text-[#0F172A] mb-3">Topics Included:</h3>
            <div className="space-y-2">
              {bundle.topics.map(topic => (
                <div key={topic.topic_id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                  <div className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-green-500" />
                    <div>
                      <p className="font-medium text-[#0F172A] text-sm">{topic.topic_name}</p>
                      <p className="text-xs text-[#64748B]">{topic.grade} • {topic.tier}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs text-[#64748B]">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {topic.card_count}
                    </span>
                    {topic.video_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Play className="w-3 h-3" />
                        {topic.video_count}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tier Selection */}
          <div className="mb-6">
            <h3 className="font-semibold text-[#0F172A] mb-3">Choose your pack:</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedTier('cards')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedTier === 'cards' 
                    ? 'border-[#C5A059] bg-[#C5A059]/5' 
                    : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-5 h-5 text-[#C5A059]" />
                  <span className="font-semibold text-[#0F172A]">Cards Only</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-[#C5A059]">
                    £{bundle.pricing.cards.price.toFixed(2)}
                  </span>
                  <span className="text-sm text-[#64748B] line-through">
                    £{bundle.pricing.cards.regular_price.toFixed(2)}
                  </span>
                </div>
              </button>
              
              <button
                onClick={() => setSelectedTier('cards_videos')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedTier === 'cards_videos' 
                    ? 'border-[#C5A059] bg-[#C5A059]/5' 
                    : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Play className="w-5 h-5 text-[#C5A059]" />
                  <span className="font-semibold text-[#0F172A]">Cards + Videos</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-[#C5A059]">
                    £{bundle.pricing.cards_videos.price.toFixed(2)}
                  </span>
                  <span className="text-sm text-[#64748B] line-through">
                    £{bundle.pricing.cards_videos.regular_price.toFixed(2)}
                  </span>
                </div>
              </button>
            </div>
          </div>

          {/* Savings Banner */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-600" />
              <span className="text-green-700 font-medium">You save</span>
            </div>
            <span className="text-xl font-bold text-green-600">£{pricing.savings.toFixed(2)}</span>
          </div>

          {/* Email for guests */}
          {!isAuthenticated && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-[#0F172A] mb-2">
                Your Email (for access link)
              </label>
              <input
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full h-11 px-4 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              />
            </div>
          )}

          {/* Buy Button */}
          <Button
            onClick={handleBuy}
            disabled={purchasing || (!isAuthenticated && !guestEmail)}
            className="w-full h-12 bg-[#C5A059] hover:bg-[#B8934E] text-white text-lg font-semibold"
          >
            {purchasing ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Buy {bundle.name} - £{pricing.price.toFixed(2)}
              </>
            )}
          </Button>

          {!isAuthenticated && (
            <p className="text-center text-sm text-[#64748B] mt-4">
              <Link to="/login" className="text-[#C5A059] hover:underline">Sign in</Link> for easier access to your purchases
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ TOPIC CARD COMPONENT ============
function TopicCard({ topic, isPurchased, purchaseTier, isInCart, onToggleCart, onSelect, onPurchase, purchasing, isAuthenticated }) {
  return (
    <div 
      className={`bg-white rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg ${
        isPurchased ? 'border-green-300' : isInCart ? 'border-[#C5A059] ring-2 ring-[#C5A059]/20' : 'border-[#E2E8F0] hover:border-[#C5A059]'
      }`}
    >
      {/* Preview Image or Placeholder */}
      <div className="aspect-[4/3] bg-gradient-to-br from-[#0F172A] to-[#334155] relative cursor-pointer" onClick={onSelect}>
        {topic.preview_image ? (
          <img 
            src={topic.preview_image.url || topic.preview_image} 
            alt={topic.topic_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-white/30" />
          </div>
        )}
        
        {/* Status Badge */}
        {isPurchased ? (
          <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <Check className="w-3 h-3" />
            {purchaseTier === 'cards_videos' ? 'Full Access' : 'Cards Owned'}
          </div>
        ) : (
          <div className="absolute top-3 right-3 bg-[#C5A059] text-white px-3 py-1 rounded-full text-xs font-medium">
            From £0.99
          </div>
        )}

        {/* Content counts */}
        <div className="absolute bottom-3 left-3 flex gap-2">
          <span className="bg-black/50 backdrop-blur text-white px-2 py-1 rounded text-xs flex items-center gap-1">
            <BookOpen className="w-3 h-3" />
            {topic.card_count} cards
          </span>
          {topic.video_count > 0 && (
            <span className="bg-black/50 backdrop-blur text-white px-2 py-1 rounded text-xs flex items-center gap-1">
              <Play className="w-3 h-3" />
              {topic.video_count} videos
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2 cursor-pointer" onClick={onSelect}>
          <h3 className="font-semibold text-[#0F172A] line-clamp-2">{topic.topic_name}</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-3">{topic.grade} • {topic.tier}</p>
        
        {!isPurchased && (
          <div className="flex items-center justify-between">
            <span className="text-[#C5A059] font-bold">£0.99</span>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleCart(); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                isInCart 
                  ? 'bg-[#C5A059] text-white' 
                  : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#C5A059]/10 hover:text-[#C5A059]'
              }`}
            >
              {isInCart ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {isInCart ? 'In Bundle' : 'Add to Bundle'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ BUNDLE CART MODAL ============
function BundleCartModal({ 
  cart, topics, bundleTier, setBundleTier, bundleEmail, setBundleEmail,
  isAuthenticated, userEmail, onRemove, onClose, onClear, onCheckout, 
  purchasing, getBundlePrice, getRegularPrice 
}) {
  const cartTopics = topics.filter(t => cart.includes(t.topic_id));
  const bundlePrice = getBundlePrice();
  const regularPrice = getRegularPrice();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-[#E2E8F0]">
          <div className="flex items-center justify-between">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-[#C5A059]" />
              Bundle Cart
            </h2>
            <button onClick={onClose} className="text-[#64748B] hover:text-[#0F172A]">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {cart.length === 0 ? (
            <div className="text-center py-8">
              <Gift className="w-12 h-12 text-[#C5A059]/40 mx-auto mb-3" />
              <p className="text-[#64748B]">Your bundle cart is empty</p>
              <p className="text-sm text-[#94A3B8] mt-1">
                Add at least 3 topics to unlock bundle pricing!
              </p>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="space-y-2 mb-6">
                {cartTopics.map(topic => (
                  <div key={topic.topic_id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                    <div>
                      <p className="font-medium text-[#0F172A] text-sm">{topic.topic_name}</p>
                      <p className="text-xs text-[#64748B]">{topic.grade} • {topic.tier}</p>
                    </div>
                    <button
                      onClick={() => onRemove(topic.topic_id)}
                      className="text-red-500 hover:text-red-600 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Tier Selection */}
              <div className="mb-6">
                <p className="text-sm font-medium text-[#0F172A] mb-2">Content Type:</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBundleTier('cards')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      bundleTier === 'cards' 
                        ? 'border-[#C5A059] bg-[#C5A059]/5' 
                        : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-4 h-4 text-[#C5A059]" />
                      <span className="font-medium text-[#0F172A]">Cards Only</span>
                    </div>
                    <p className="text-xs text-[#64748B]">
                      3 for £2.50 • 5 for £3.99
                    </p>
                  </button>
                  <button
                    onClick={() => setBundleTier('cards_videos')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      bundleTier === 'cards_videos' 
                        ? 'border-[#C5A059] bg-[#C5A059]/5' 
                        : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Play className="w-4 h-4 text-[#C5A059]" />
                      <span className="font-medium text-[#0F172A]">Cards + Videos</span>
                    </div>
                    <p className="text-xs text-[#64748B]">
                      3 for £9.99 • 5 for £14.99
                    </p>
                  </button>
                </div>
              </div>

              {/* Pricing Summary */}
              <div className="bg-[#F8FAFC] rounded-xl p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#64748B]">{cart.length} topics @ £{bundleTier === 'cards' ? '0.99' : '3.99'} each</span>
                  <span className="text-[#64748B] line-through">£{regularPrice.toFixed(2)}</span>
                </div>
                {bundlePrice ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-[#0F172A]">Bundle Price</span>
                      <span className="text-2xl font-bold text-[#C5A059]">£{bundlePrice.price.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-end gap-1 text-green-600 text-sm mt-1">
                      <Sparkles className="w-4 h-4" />
                      <span>You save £{bundlePrice.savings.toFixed(2)}!</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-amber-600 text-sm">
                    Add {3 - cart.length} more topic{3 - cart.length > 1 ? 's' : ''} to unlock bundle pricing
                  </div>
                )}
              </div>

              {/* Email for guests */}
              {!isAuthenticated && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">
                    Your Email (for access link)
                  </label>
                  <input
                    type="email"
                    value={bundleEmail}
                    onChange={(e) => setBundleEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full h-11 px-4 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClear}
                  className="flex-1 border-[#E2E8F0]"
                >
                  Clear Cart
                </Button>
                <Button
                  onClick={onCheckout}
                  disabled={purchasing || cart.length < 3 || (!isAuthenticated && !bundleEmail)}
                  className="flex-1 bg-[#C5A059] hover:bg-[#B8934E] text-white"
                >
                  {purchasing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Checkout
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ TOPIC MODAL COMPONENT ============
function TopicModal({ topic, isPurchased, purchaseTier, onClose, onPurchase, purchasing, isAuthenticated, userEmail }) {
  const [selectedTier, setSelectedTier] = useState('cards');
  const [guestEmail, setGuestEmail] = useState('');

  const handleBuy = () => {
    const email = isAuthenticated ? userEmail : guestEmail;
    if (!isAuthenticated && !guestEmail) {
      alert('Please enter your email address');
      return;
    }
    onPurchase(topic.topic_id, selectedTier, email);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative">
          <div className="aspect-video bg-gradient-to-br from-[#0F172A] to-[#334155]">
            {topic.preview_image ? (
              <img 
                src={topic.preview_image.url || topic.preview_image} 
                alt={topic.topic_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-16 h-16 text-white/30" />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-black/70"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-2 text-sm text-[#64748B] mb-2">
            <span className="px-2 py-1 bg-[#F1F5F9] rounded">{topic.grade}</span>
            <span className="px-2 py-1 bg-[#F1F5F9] rounded">{topic.tier}</span>
          </div>
          
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-2">
            {topic.topic_name}
          </h2>
          
          {topic.description && (
            <p className="text-[#64748B] mb-4">{topic.description}</p>
          )}

          {/* Content Summary */}
          <div className="flex gap-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-[#64748B]">
              <BookOpen className="w-4 h-4 text-[#C5A059]" />
              <span>{topic.card_count} Revision Cards</span>
            </div>
            {topic.video_count > 0 && (
              <div className="flex items-center gap-2 text-sm text-[#64748B]">
                <Play className="w-4 h-4 text-[#C5A059]" />
                <span>{topic.video_count} Videos</span>
              </div>
            )}
          </div>

          {isPurchased ? (
            /* Already Purchased */
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <h3 className="font-semibold text-green-800 mb-1">You Own This!</h3>
              <p className="text-green-600 text-sm mb-3">
                {purchaseTier === 'cards_videos' ? 'Full access to cards and videos' : 'Access to revision cards'}
              </p>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-green-700 font-medium hover:underline"
              >
                View in Dashboard <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            /* Purchase Options */
            <>
              <div className="space-y-3 mb-6">
                {/* Cards Only Option */}
                <div
                  onClick={() => setSelectedTier('cards')}
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    selectedTier === 'cards' 
                      ? 'border-[#C5A059] bg-[#C5A059]/5' 
                      : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedTier === 'cards' ? 'border-[#C5A059] bg-[#C5A059]' : 'border-[#CBD5E1]'
                      }`}>
                        {selectedTier === 'cards' && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0F172A]">Revision Cards Only</h4>
                        <p className="text-sm text-[#64748B]">{topic.card_count} worked examples</p>
                      </div>
                    </div>
                    <span className="text-xl font-bold text-[#C5A059]">£0.99</span>
                  </div>
                </div>

                {/* Cards + Videos Option */}
                {topic.has_videos && (
                  <div
                    onClick={() => setSelectedTier('cards_videos')}
                    className={`border-2 rounded-xl p-4 cursor-pointer transition-all relative ${
                      selectedTier === 'cards_videos' 
                        ? 'border-[#C5A059] bg-[#C5A059]/5' 
                        : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
                    }`}
                  >
                    <div className="absolute -top-2 left-4 bg-[#C5A059] text-white text-xs font-bold px-2 py-0.5 rounded">
                      BEST VALUE
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedTier === 'cards_videos' ? 'border-[#C5A059] bg-[#C5A059]' : 'border-[#CBD5E1]'
                        }`}>
                          {selectedTier === 'cards_videos' && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#0F172A]">Cards + Videos Bundle</h4>
                          <p className="text-sm text-[#64748B]">{topic.card_count} cards + {topic.video_count} walkthrough videos</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-[#C5A059]">£3.99</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Guest Email Input */}
              {!isAuthenticated && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">
                    Your Email (for access link)
                  </label>
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="guest-email-input"
                    className="w-full h-11 px-4 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                  />
                  <p className="text-xs text-[#64748B] mt-1">
                    Or <Link to="/login" className="text-[#C5A059] hover:underline">sign in</Link> for easier access
                  </p>
                </div>
              )}

              {/* Buy Button */}
              <Button
                onClick={handleBuy}
                disabled={purchasing || (!isAuthenticated && !guestEmail)}
                data-testid="buy-now-button"
                className="w-full h-12 bg-[#C5A059] hover:bg-[#B8934E] text-white font-semibold"
              >
                {purchasing ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Buy Now - £{selectedTier === 'cards_videos' ? '3.99' : '0.99'}
                  </span>
                )}
              </Button>

              <p className="text-center text-xs text-[#94A3B8] mt-3">
                Secure payment via Stripe. Instant access after purchase.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
